import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, Play, ExternalLink } from "lucide-react";
import { UserAvatar } from "../common/UserAvatar";
import type { QueueSong, UserRole } from "../../types";

interface QueueItemCardProps {
  song: QueueSong;
  index: number;
  currentUserId?: string;
  userRole: UserRole | null;
  onDelete: (id: string) => void;
  onPlayNow?: (song: QueueSong) => void;
}

export const QueueItemCard: React.FC<QueueItemCardProps> = ({
  song,
  index,
  currentUserId,
  userRole,
  onDelete,
  onPlayNow,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: song.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 1,
    opacity: isDragging ? 0.6 : 1,
  };

  const isOwner = currentUserId === song.addedByUserId;
  const isHost = userRole === "HOST";
  const canDelete = isHost || isOwner;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group bg-card p-3 sm:p-4 rounded-2xl border transition-all flex items-center gap-3 sm:gap-4 shadow-md ${
        index === 0
          ? "border-primary/50 bg-primary/5 glow-primary"
          : "border-border hover:border-accent/40"
      }`}
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="p-1.5 rounded-lg text-text-muted hover:text-primary active:text-primary cursor-grab active:cursor-grabbing shrink-0 touch-none"
        title="Drag to reorder queue"
      >
        <GripVertical className="w-5 h-5" />
      </button>

      {/* Index Number */}
      <span className="text-sm font-bold font-mono text-text-muted w-5 text-center shrink-0">
        {index + 1}
      </span>

      {/* Thumbnail */}
      <div className="relative w-16 h-12 sm:w-20 sm:h-14 rounded-xl overflow-hidden bg-background shrink-0 shadow-sm">
        <img
          src={song.thumbnailUrl}
          alt={song.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {!song.embeddable && (
          <span
            className="absolute top-1 right-1 bg-red-600/90 text-white text-[8px] font-bold px-1 rounded"
            title="Non-embeddable track"
          >
            EXT
          </span>
        )}
      </div>

      {/* Details */}
      <div className="min-w-0 flex-1 space-y-0.5">
        <h4 className="text-xs sm:text-sm font-bold text-text-main truncate">
          {song.title}
        </h4>
        <p className="text-[11px] text-text-muted truncate">
          {song.channelTitle}
        </p>
        <div className="flex items-center gap-1.5 pt-0.5">
          <UserAvatar
            photoURL={song.addedByUserPhoto}
            name={song.addedByUserName}
            uid={song.addedByUserId}
            size="xs"
          />
          <span className="text-[10px] text-text-muted">
            Added by <strong className="text-text-main font-medium">{song.addedByUserName}</strong>
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        {isHost && onPlayNow && (
          <button
            onClick={() => onPlayNow(song)}
            className="p-2 rounded-xl text-accent hover:bg-accent/10 transition-colors cursor-pointer"
            title="Play Song"
          >
            <Play className="w-4 h-4" />
          </button>
        )}

        {!song.embeddable && (
          <a
            href={`https://www.youtube.com/watch?v=${song.videoId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-xl text-yellow-400 hover:bg-yellow-400/10 transition-colors"
            title="Open in YouTube"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        )}

        {canDelete && (
          <button
            onClick={() => onDelete(song.id)}
            className="p-2 rounded-xl text-text-muted hover:text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
            title={isHost ? "Remove song (Host)" : "Remove my song"}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};
